import { useRef } from 'react'
import {
  Carousel,
  CarouselSlide,
  Column,
  FlexBox,
  H2,
  Link,
  Row,
  Typography,
  useScreenClass
} from '@vp/swan'
import ProductTileCollectionUtil from '../utils/productTileCollection/productTileCollection'
import { useAppContext } from '../state/AppContext'
import { isProductTileAvailable, isProductTileInStock } from '../utils/ProductTile/productTile'
import { renderProductDebugError } from './errorComponents/ProductGridLaneError'
import { getAccessibleTextForDots } from '../utils/accessibility/accessibility'
import { DebugPageSections } from './debugComponents/DebugPageSections'
import { SkipToNextSectionLink } from './SkipToNextSectionLink'
import { ProductTileV2 } from './ProductTileV2'
import { ProductLaneError } from './errorComponents/ProductLaneError'
import { ProductLaneModel } from './AutoGenerate/types/ProductLaneModel'
import { ProductLaneType } from '../types/TemplateConfiguration'
import { buildProductCallout } from '../utils/callouts/productCalloutBuilder'
import { ProductCallout } from './AutoGenerate/types/AutomatedExperience'

export interface ProductLaneProps {
  sectionData: ProductLaneModel | undefined
  key: number
  rawSectionData?: ProductLaneType
}

export const ProductLane = ({ sectionData, rawSectionData }: ProductLaneProps) => {
  const { sidebarEnabled, dictionaryEntries, debugMode, logger } = useAppContext()
  const imageRef = useRef<HTMLImageElement>(null)
  const screenClass = useScreenClass()
  const isMobile = screenClass === 'xs'
  const arrowVerticalPosition =
    imageRef.current?.clientHeight && imageRef.current?.clientHeight > 0
      ? `${Math.round(imageRef.current.clientHeight / 2)}px`
      : null

  if (!sectionData?.tiles?.length) {
    return <></>
  }

  let tilesCount = sectionData.tiles.length
  sectionData.tiles.forEach((tile) => {
    if (!debugMode && !isProductTileAvailable(tile)) {
      tilesCount--
    }
  })

  const desktopTilePerRow = ProductTileCollectionUtil.getNumberOfProductTilesPerRow(
    sidebarEnabled,
    sectionData.type,
    tilesCount
  )

  // The slidesToShow property now supports responsive values, helps prevent flicker on page load.
  const slidestoShowCount = {
    xs: 2.1,
    sm: desktopTilePerRow,
    md: desktopTilePerRow,
    lg: desktopTilePerRow,
    xl: desktopTilePerRow
  }

  const {
    accessibleTextForNext,
    accessibleTextForPrevious,
    accessibleTextForSlide,
    accessibleTextForVisibleSlides,
    productLane,
    carousel,
    slide
  } = dictionaryEntries
  const accessibleTextForDots = getAccessibleTextForDots(
    dictionaryEntries?.['accessibleTextForDots'] ?? '',
    sectionData.tiles.length
  )
  const hideProductDescription = sectionData.hideProductDescription
  const colorSwatchCount = ProductTileCollectionUtil.getMaxColorSwatchCountDesktop(
    desktopTilePerRow,
    sidebarEnabled
  )

  const carouselId = `${productLane}-${sectionData.sectionNumber}`
  const carouselTitle = sectionData.title || `${productLane} ${sectionData.sectionNumber}`
  const carouselLabel = `${carouselTitle}`

  return (
    <>
      <Row mb={4} data-testid={`product-lane-title-row-${sectionData.sectionId}`}>
        <Column span={sidebarEnabled ? 7 : 5}>
          {sectionData.title && <H2 fontSize="x2large">{sectionData.title}</H2>}
        </Column>
      </Row>
      <SkipToNextSectionLink
        nextSectionId={sectionData.nextSectionId}
        nextSectionTitle={sectionData?.nextSectionTitle}
      />
      <Row mb={7}>
        <Column span={sidebarEnabled ? 7 : 5}>
          {sectionData.subtitle && (
            <Typography fontSize="standard">{sectionData.subtitle}</Typography>
          )}
        </Column>
        <Column span={sidebarEnabled ? 5 : 7}>
          <FlexBox alignItems="flex-end" justifyContent="flex-end">
            {sectionData.enableSeeAllCTA &&
              sectionData.seeAllCta?.targetUrl &&
              dictionaryEntries['seeAllCtaText'] && (
                <Link skin="cta" href={sectionData.seeAllCta?.targetUrl}>
                  {dictionaryEntries['seeAllCtaText']}
                </Link>
              )}
          </FlexBox>
        </Column>
      </Row>
      <Carousel
        id={carouselId}
        role="region"
        slidesToShow={slidestoShowCount}
        arrows={!isMobile}
        swipeToSlide
        gridGutters
        infinite={false}
        initialSlide={0}
        arrowVerticalPosition={arrowVerticalPosition}
        progressIndicator={isMobile ? 'dots' : 'none'}
        accessibleTextForPrevious={accessibleTextForPrevious ?? ''}
        accessibleTextForDots={accessibleTextForDots}
        accessibleTextForVisibleSlides={accessibleTextForVisibleSlides ?? null}
        accessibleTextForNext={accessibleTextForNext ?? ''}
        aria-roledescription={(carousel as string) ?? null}
        aria-labelledby={`${carouselId}`}
        aria-label={carouselLabel}
        accessibleTextForSlide={accessibleTextForSlide ?? null}
      >
        {sectionData.tiles.map((tile, i) => {
          if (isProductTileAvailable(tile)) {
            const calloutDetails = buildProductCallout({
              isOutOfStock: tile?.isOutOfStock ?? false,
              outOfStockText: dictionaryEntries?.['outOfStock'] || '',
              logger,
              callout: tile?.callout as ProductCallout | ProductCallout[],
              allAvailableTags: tile?.allAvailableTags,
              tileId: tile?.contentful_id
            })
            const slideId = `${carouselId}-${slide}-${i + 1}`
            const productName = tile.name
            const newTileMpvId = tile.mpvId
            return (
              <CarouselSlide
                role="group"
                aria-roledescription={slide}
                aria-label={`${slide} ${i + 1} of ${sectionData.tiles.length}: ${productName || ''}`}
                id={slideId}
                key={i}
              >
                <ProductTileV2
                  name={tile.name ?? ''}
                  description={!hideProductDescription ? (tile.description ?? '') : ''}
                  formattedDescription={ProductTileCollectionUtil.getFormattedDescription(
                    hideProductDescription,
                    tile.formattedDescription
                  )}
                  image={tile.image}
                  linkUrl={tile.linkUrl}
                  callout={calloutDetails}
                  layout="vertical"
                  disabled={!isProductTileInStock(tile)}
                  index={i + 1}
                  imageRef={imageRef}
                  pageZone={`Product Lane ${sectionData.sectionNumber}`}
                  mpvId={newTileMpvId}
                  review={tile.review}
                  productId={tile.productId}
                  showPrice={!!tile.showPrice}
                  enableCampaignCallouts={tile?.enableCampaignCallouts || true}
                  colorInformation={tile.colorInformation}
                  colorSwatchCount={colorSwatchCount}
                  version={tile.version}
                  selections={tile.selectedProductVariants}
                  quantity={tile.quantity}
                />
              </CarouselSlide>
            )
          } else if (renderProductDebugError(tile)) {
            return (
              <CarouselSlide
                role="group"
                aria-roledescription="slide"
                aria-label={`${slide} ${i + 1} of ${sectionData.tiles.length}: Error slide`}
                key={i}
              >
                <ProductLaneError
                  tile={tile}
                  index={i}
                  hideProductDescription={hideProductDescription}
                  colorSwatchCount={colorSwatchCount}
                />
              </CarouselSlide>
            )
          }
          return null
        })}
      </Carousel>
      <DebugPageSections rawSectionData={rawSectionData ?? {}} resolvedSectionData={sectionData} />
    </>
  )
}
